/**
 * Stub for task actions that were part of the old Electric SQL sync system.
 * This file provides compatibility functions for creating tasks with labels.
 */

import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import type { LabelGroupDocument } from '@/lib/db/rxdb/schemas/labelGroup'

/**
 * Task data for creation (includes description for content creation)
 */
interface TaskCreateData {
  id?: string
  name: string
  description?: string
  descriptionFormat?: string
  descriptionLength?: number
  descriptionPlainText?: string | null
  [key: string]: any // Allow other fields
}

interface CreateTaskWithLabelsParams {
  task: TaskCreateData
  labelIds: Array<string>
  createTask: (data: any) => Promise<any>
  createTaskLabel: (data: any) => Promise<any>
  createTaskContent?: (data: any) => Promise<any>
  labels?: Array<LabelDocument>
  labelGroups?: Array<LabelGroupDocument>
}

/**
 * Creates a task and associates labels with it in one operation.
 * This is now implemented using RxDB mutations instead of Electric SQL.
 *
 * Note: This function requires mutation functions to be passed as parameters.
 * Call useTaskMutations(), useTaskLabelMutations(), and useTaskContentMutations()
 * in your component and pass the mutation functions here.
 */
export async function createTaskWithLabels({
  task,
  labelIds,
  createTask,
  createTaskLabel,
  createTaskContent,
  labels,
  labelGroups,
}: CreateTaskWithLabelsParams): Promise<void> {

  try {
    // Extract description fields before creating task
    const { description, descriptionFormat, descriptionLength, descriptionPlainText, ...taskWithoutDescription } = task
    const taskId = task.id

    if (!taskId) {
      throw new Error('[createTaskWithLabels] Task ID is required')
    }

    // Create task WITHOUT description fields (schema v3 removed them)
    await createTask(taskWithoutDescription)

    // Create content separately if description exists
    // Note: Backend also handles content creation during sync, but we create it here
    // for immediate local availability
    if (createTaskContent && description) {

      const contentData = {
        taskId: taskId, // Reference to the task
        content: description,
        format: descriptionFormat || 'tiptap',
        length: descriptionLength || 0,
        plainText: descriptionPlainText || null,
        version: 1, // Initial version
      }

      try {
        await Promise.race([
          createTaskContent(contentData),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Content creation timeout after 5s')), 5000)
          )
        ])
      } catch (contentError) {
        // Error handling
        throw contentError
      }
    } else {
    }

    // Then create label associations
    const labelMap = new Map(labels?.map((label) => [label.id, label]) ?? [])
    const labelGroupMap = new Map(
      labelGroups?.map((group) => [group.id, group]) ?? [],
    )

    if (labelIds.length > 0) {
      for (const labelId of labelIds) {
        const label = labelMap.get(labelId)
        const labelGroupId = label?.labelGroupId ?? null
        const labelGroupName = labelGroupId
          ? labelGroupMap.get(labelGroupId)?.name ?? null
          : null

        await createTaskLabel({
          taskId: taskId,
          labelId: labelId,
          labelName: label?.name || '',
          labelColor: label?.color || '#gray',
          labelGroupId,
          labelGroupName,
        })
      }
    }

  } catch (error) {
    // Error handling
    throw error
  }
}
